import { NetworkConfiguration } from "../networkConfriguation";
import { callAPI } from "../networkConnection";

/**
 * Alibaba Product Subscription API Service
 * Handles all Alibaba product subscription operations
 */
class AlibabaSubscriptionApiService {
  /**
   * Subscribe to an Alibaba product
   * @param {string} offerId - Alibaba offer ID
   * @returns {Promise} API response
   */
  static subscribeToProduct(offerId, priceRanges, fromPDP, searchKeyword = '', sp, variations, title = '') {
    return new Promise((resolve, reject) => {
      const qs = new URLSearchParams();
      if (searchKeyword) qs.set('search', searchKeyword);
      const queryString = qs.toString();
      const url = process.env.REACT_APP_APIUrl + NetworkConfiguration.ALIBABA_SUBSCRIBE_PRODUCT + `/${offerId}` + (queryString ? `?${queryString}` : '');
      callAPI(
        url,
        "POST",
        {
          "Content-Type": "application/json",
        },
        JSON.stringify({ offerId, priceRanges, fromPDP, sp, variations, title})
      )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          console.error("Error subscribing to Alibaba product:", err);
          reject(err);
        });
    });
  }

  /**
   * Unsubscribe from an Alibaba product
   * @param {string} offerId - Alibaba offer ID
   * @returns {Promise} API response
   */
  static unsubscribeFromProduct(offerId) {
    return new Promise((resolve, reject) => {
      callAPI(
        process.env.REACT_APP_APIUrl + NetworkConfiguration.ALIBABA_UNSUBSCRIBE_PRODUCT + `/${offerId}`,
        "POST",
        {
          "Content-Type": "application/json",
        }
      )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          console.error("Error unsubscribing from Alibaba product:", err);
          reject(err);
        });
    });
  }

  /**
   * Get user's Alibaba product drafts
   * @param {Object} options - Query options (page, limit)
   * @returns {Promise} API response
   */
  // static getUserDrafts(options = {}) {
  //   return new Promise((resolve, reject) => {
  //     const { page = 1, limit = 20 } = options;
  //     const queryParams = new URLSearchParams({
  //       page: page.toString(),
  //       limit: limit.toString()
  //     });

  //     callAPI(
  //       process.env.REACT_APP_APIUrl + NetworkConfiguration.ALIBABA_GET_DRAFTS + `?${queryParams}`,
  //       "GET",
  //       {
  //         "Content-Type": "application/json",
  //       }
  //     )
  //       .then((res) => {
  //         resolve(res);
  //       })
  //       .catch((err) => {
  //         console.error("Error getting Alibaba drafts:", err);
  //         reject(err);
  //       });
  //   });
  // }

  /**
   * Get specific Alibaba product draft by offer ID
   * @param {string} offerId - Alibaba offer ID
   * @returns {Promise} API response
   */
  // static getDraftByOfferId(offerId) {
  //   return new Promise((resolve, reject) => {
  //     callAPI(
  //       process.env.REACT_APP_APIUrl + NetworkConfiguration.ALIBABA_GET_DRAFT + `/${offerId}`,
  //       "GET",
  //       {
  //         "Content-Type": "application/json",
  //       }
  //     )
  //       .then((res) => {
  //         resolve(res);
  //       })
  //       .catch((err) => {
  //         console.error("Error getting Alibaba draft:", err);
  //         reject(err);
  //       });
  //   });
  // }

  /**
   * Check subscription status for an Alibaba product (checks both products and drafts tables)
   * @param {string} offerId - Alibaba offer ID
   * @returns {Promise} API response
   */
  static checkSubscriptionStatus(offerId) {
    return new Promise((resolve, reject) => {
      callAPI(
        process.env.REACT_APP_APIUrl + NetworkConfiguration.ALIBABA_CHECK_SUBSCRIPTION + `/${offerId}`,
        "GET",
        {
          "Content-Type": "application/json",
        }
      )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          console.error("Error checking subscription status:", err);
          reject(err);
        });
    });
  }

  /**
   * Regenerate AI content for an Alibaba product draft
   * @param {string} offerId - Alibaba offer ID
   * @returns {Promise} API response
   */
  static regenerateAiContent(offerId) {
    return new Promise((resolve, reject) => {
      callAPI(
        process.env.REACT_APP_APIUrl + NetworkConfiguration.ALIBABA_REGENERATE_CONTENT + `/${offerId}`,
        "POST",
        {
          "Content-Type": "application/json",
        }
      )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          console.error("Error regenerating AI content:", err);
          reject(err);
        });
    });
  }

  /**
   * Subscribe to multiple Alibaba products with category mapping
   * @param {Array} products - Array of product objects with sku/offerId
   * @param {string} categoryId - Category ID to assign to products
   * @returns {Promise} API response
   */
  static subscribeToMultipleProducts(products, categoryId) {
    return new Promise((resolve, reject) => {
      const payload = {
        products: products.map(product => ({
          offerId: product.sku || product.offerId,
          categoryId: categoryId,
          // Include additional product data that might be useful
          title: product.subjectTrans || product.title,
          imageUrl: product.imageUrl || product.imgUrl?.[0],
          price: product.priceInfo?.consignPrice || product.consign_price || product.priceInfo?.price || product.price,
          minOrderQuantity: product.minOrderQuantity || product.moq,
          monthSold: product.monthSold || product.month_sold
        })),
        batchOperation: true
      };

      callAPI(
        process.env.REACT_APP_APIUrl + NetworkConfiguration.ALIBABA_SUBSCRIBE_MULTIPLE,
        "POST",
        {
          "Content-Type": "application/json",
        },
        JSON.stringify(payload)
      )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          console.error("Error subscribing to multiple Alibaba products:", err);
          reject(err);
        });
    });
  }

  /**
   * Fallback: Subscribe to multiple products sequentially
   * @param {Array} products - Array of product objects
   * @param {string} categoryId - Category ID to assign
   * @returns {Promise} API response summary
   */
  static subscribeToMultipleProductsSequential(products, categoryId) {
    return new Promise(async (resolve, reject) => {
      const results = {
        successful: [],
        failed: [],
        total: products.length
      };

      try {
        for (const product of products) {
          try {
            const result = await this.subscribeToProduct(product.sku || product.offerId);
            if (result.status?.code === 200) {
              results.successful.push({
                product,
                result
              });
            } else {
              results.failed.push({
                product,
                error: result.error?.message || 'Subscription failed'
              });
            }
          } catch (error) {
            results.failed.push({
              product,
              error: error.message || 'Network error'
            });
          }

          // Add small delay between requests to avoid overwhelming the server
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        // Return summary
        resolve({
          status: {
            code: results.failed.length === 0 ? 200 : (results.successful.length > 0 ? 206 : 400),
            message: `${results.successful.length} products subscribed successfully, ${results.failed.length} failed`
          },
          data: results
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Subscribe to multiple Alibaba products with keyword and category metadata
   * @param {Array} products - Array of product objects with sku/offerId
   * @param {string} categoryId - Category ID to assign to products
   * @param {string} keyword - Search keyword used to find these products
   * @param {string} categoryName - Full category name/path for display
   * @returns {Promise} API response
   */
  static subscribeToMultipleProductsWithKeyword(products, categoryId, keyword, categoryName) {
    return new Promise((resolve, reject) => {
      const payload = {
        products: products.map(product => ({
          offerId: product.sku || product.offerId,
          categoryId: categoryId,
          keyword: keyword,
          selectedCategoryId: categoryId,
          selectedCategoryName: categoryName,
          // Include additional product data that might be useful
          title: product.subjectTrans || product.title,
          imageUrl: product.imageUrl || product.imgUrl?.[0],
          price: product.priceInfo?.consignPrice || product.consign_price || product.priceInfo?.price || product.price,
          minOrderQuantity: product.minOrderQuantity || product.moq,
          monthSold: product.monthSold || product.month_sold
        })),
        batchOperation: true,
        keyword: keyword,
        categoryMapping: {
          categoryId: categoryId,
          categoryName: categoryName
        }
      };

      callAPI(
        process.env.REACT_APP_APIUrl + NetworkConfiguration.ALIBABA_SUBSCRIBE_MULTIPLE,
        "POST",
        {
          "Content-Type": "application/json",
        },
        JSON.stringify(payload)
      )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          console.error("Error subscribing to multiple Alibaba products with keyword:", err);
          reject(err);
        });
    });
  }

  /**
   * Subscribe to multiple products with metadata (matches backend API format)
   * @param {Array} products - Array of {product_id, keyword, category_id, generated_description, alibaba_original_data}
   * @returns {Promise} API response
   */
  static subscribeToMultipleProductsWithMetadata(products) {
    return new Promise((resolve, reject) => {
      const payload = { 
        products,
        includeDescriptions: true // Flag to indicate descriptions are included
      };

      callAPI(
        process.env.REACT_APP_APIUrl + NetworkConfiguration.ALIBABA_SUBSCRIBE_MULTIPLE,
        "POST",
        {
          "Content-Type": "application/json",
        },
        JSON.stringify(payload)
      )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          console.error("Error subscribing to multiple Alibaba products:", err);
          reject(err);
        });
    });
  }

  /**
   * Fallback: Subscribe to multiple products sequentially with keyword metadata
   * @param {Array} products - Array of product objects
   * @param {string} categoryId - Category ID to assign
   * @param {string} keyword - Search keyword
   * @param {string} categoryName - Category display name
   * @returns {Promise} API response summary
   */
  static subscribeToMultipleProductsSequentialWithKeyword(products, categoryId, keyword, categoryName) {
    return new Promise(async (resolve, reject) => {
      const results = {
        successful: [],
        failed: [],
        total: products.length
      };

      try {
        for (const product of products) {
          try {
            // For sequential calls, we'll use the existing single product API
            // but could extend it to include keyword metadata if needed
            const result = await this.subscribeToProduct(product.sku || product.offerId);
            if (result.status?.code === 200) {
              results.successful.push({
                product,
                result,
                keyword,
                categoryId,
                categoryName
              });
            } else {
              results.failed.push({
                product,
                keyword,
                error: result.error?.message || 'Subscription failed'
              });
            }
          } catch (error) {
            results.failed.push({
              product,
              keyword,
              error: error.message || 'Network error'
            });
          }

          // Add small delay between requests to avoid overwhelming the server
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        // Return summary with keyword context
        resolve({
          status: {
            code: results.failed.length === 0 ? 200 : (results.successful.length > 0 ? 206 : 400),
            message: `${results.successful.length} products subscribed successfully for keyword "${keyword}", ${results.failed.length} failed`
          },
          data: results,
          keyword,
          categoryId,
          categoryName
        });
      } catch (error) {
        reject(error);
      }
    });
  }
}

export default AlibabaSubscriptionApiService;
